/**
 * Turn the assistant's inline markup tokens into render nodes.
 *
 * Two grammars, one splitter. An **entity reference** names a row the user can
 * see (`<merchant_id=UUID />`) and the client resolves its label, so the model
 * never authors a name that disagrees with the row and the id is the only thing
 * it has to get right. A **suggestion** (`<suggestion="…" />`) names an action
 * instead, so there its text *is* the payload — those exact words are what the
 * user clicks and what gets sent back.
 *
 * The **date macro** (`<date=2026-08-18 />`) rides the same text-node walk but
 * is a substitution, not a split: it becomes words in place, so no node is
 * emitted and no renderer registers for it.
 *
 * **Why this shape survives.** micromark rejects both as HTML — a tag name
 * cannot be followed by `=` — so they arrive intact inside ordinary `text`
 * nodes rather than being classified as raw HTML and dropped. The underscore in
 * the entity tokens is load-bearing for the same reason; a hyphen is a legal
 * tag-name character, so `<merchant-id …>` could be nudged into HTML parsing by
 * a different attribute shape. That also means a token can only be reached by
 * splitting text nodes: there is no markdown construct to hook.
 *
 * Emitted nodes carry `data.hName` / `data.hProperties`, which `mdast-util-to-hast`
 * copies onto a hast element verbatim — so `AssistantMessage` renders them by
 * registering {@link ENTITY_REF_TAG} and {@link SUGGESTION_TAG} in
 * `react-markdown`'s `components` map.
 */

import { renderDateMacros } from '@orn/global/assistant/dateMacro.js';
import { entityRefPattern, suggestionPattern, type EntityRefType } from '@orn/global/assistant/inlineMarkup.js';

export type { EntityRefType };

/** The hast tag name emitted for a resolved entity token — a `components` map key. */
export const ENTITY_REF_TAG = 'entity-ref';

/** The hast tag name emitted for a suggested next action — a `components` map key. */
export const SUGGESTION_TAG = 'assistant-suggestion';

/** Both grammars in one pass, so a message mixing them splits left to right. */
const anyTokenPattern = (): RegExp => new RegExp(`${entityRefPattern().source}|${suggestionPattern().source}`, 'g');

/**
 * A token still arriving, anchored to the end of the text. Deliberately loose:
 * it only ever runs mid-stream, where hiding one keystroke of prose costs
 * nothing and showing half a token costs the illusion that the assistant is
 * writing prose.
 *
 * The absent `>` is what distinguishes a partial from a finished token, so the
 * quoted arm spans anything but that — a suggestion's text may itself contain
 * quotes, and stopping at the first one would un-hide the rest of the token.
 */
const TRAILING_PARTIAL_PATTERN = /<[a-z_]*(?:=(?:"[^>]*|[^\s/>]*))?\s*\/?$/;

interface ITextNode {
  type: 'text';
  value: string;
}

interface IParentNode {
  type: string;
  children?: unknown[];
}

interface IEntityRefNode {
  type: 'entityRef';
  data: {
    hName: typeof ENTITY_REF_TAG;
    hProperties: { refType: EntityRefType; refId: string };
  };
}

interface ISuggestionNode {
  type: 'assistantSuggestion';
  data: {
    hName: typeof SUGGESTION_TAG;
    hProperties: { suggestionText: string };
  };
}

type MarkupNode = IEntityRefNode | ISuggestionNode;

const isParent = (node: unknown): node is IParentNode =>
  typeof node === 'object' && node !== null && Array.isArray((node as IParentNode).children);

const isText = (node: unknown): node is ITextNode =>
  typeof node === 'object' && node !== null && (node as ITextNode).type === 'text';

/** Split one text node's value into alternating text and markup nodes. */
function splitTokens(value: string): (ITextNode | MarkupNode)[] {
  const out: (ITextNode | MarkupNode)[] = [];
  let cursor = 0;
  // `matchAll` on a /g/ regex walks the string once and hands back each match
  // with its index, which is all the bookkeeping this needs. Capture groups 1-2
  // belong to the entity alternative, group 3 to the suggestion one — whichever
  // alternative matched leaves the other undefined.
  for (const match of value.matchAll(anyTokenPattern())) {
    const start = match.index;
    if (start > cursor) {
      out.push({ type: 'text', value: value.slice(cursor, start) });
    }
    out.push(
      match[1] === undefined
        ? {
            type: 'assistantSuggestion',
            data: { hName: SUGGESTION_TAG, hProperties: { suggestionText: match[3] } },
          }
        : {
            type: 'entityRef',
            data: { hName: ENTITY_REF_TAG, hProperties: { refType: match[1] as EntityRefType, refId: match[2] } },
          }
    );
    cursor = start + match[0].length;
  }
  if (cursor < value.length) {
    out.push({ type: 'text', value: value.slice(cursor) });
  }
  return out;
}

/** Every `text` node in the tree, in document order, with where it sits. */
function collectTextNodes(root: unknown): { parent: IParentNode; index: number }[] {
  const found: { parent: IParentNode; index: number }[] = [];
  const walk = (node: unknown): void => {
    if (!isParent(node)) {
      return;
    }
    node.children?.forEach((child, index) => {
      if (isText(child)) {
        found.push({ parent: node, index });
      } else {
        walk(child);
      }
    });
  };
  walk(root);
  return found;
}

export interface IRemarkAssistantMarkupOptions {
  /**
   * Drop a token still mid-arrival from the end of the message. Set from the
   * bubble's streaming flag: a finalized message keeps a trailing `<` because
   * there it is prose the user actually wrote or the model actually meant.
   */
  suppressTrailingPartial?: boolean;
  /**
   * Reader clock for `<date=… />` substitution. Runs on the same text nodes
   * this plugin already walks, so a token quoted inside a code span or fence
   * is left alone for the same reason a chip token there is. Omitted, date
   * macros pass through as literal text.
   */
  dateMacroContext?: { now: Date; timeZone: string };
}

/**
 * Remark plugin. Splits every `text` node on the entity-token grammar and
 * leaves the rest of the tree alone — code spans and fenced blocks carry their
 * content on `value` rather than in `text` children, so a token quoted inside
 * one is never touched.
 */
export default function remarkAssistantMarkup(options: IRemarkAssistantMarkupOptions = {}) {
  return (tree: unknown): void => {
    const textNodes = collectTextNodes(tree);
    if (textNodes.length === 0) {
      return;
    }

    if (options.suppressTrailingPartial === true) {
      const last = textNodes[textNodes.length - 1];
      const node = last.parent.children![last.index] as ITextNode;
      node.value = node.value.replace(TRAILING_PARTIAL_PATTERN, '');
    }

    // Reverse order so a splice never shifts an index still to be visited.
    for (let i = textNodes.length - 1; i >= 0; i -= 1) {
      const { parent, index } = textNodes[i];
      const node = parent.children![index] as ITextNode;
      if (options.dateMacroContext !== undefined) {
        node.value = renderDateMacros(node.value, options.dateMacroContext);
      }
      const replacement = splitTokens(node.value);
      const unchanged = replacement.length === 1 && replacement[0].type === 'text';
      if (!unchanged) {
        parent.children!.splice(index, 1, ...replacement);
      }
    }
  };
}
